import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { PostImplEditorRole } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { isChangeManager } from '../changePermissions';
import {
  DEFAULT_POST_IMPL_EDITOR_ROLES,
  normalizePostImplEditorRoles,
  POST_IMPL_EDITOR_ROLE_OPTIONS,
} from '../changePostImplEditorRoles';

interface Props {
  initialRoles?: PostImplEditorRole[];
  onRolesLoaded?: (roles: PostImplEditorRole[]) => void;
}

const ChangePostImplEditorRolesPanel: React.FC<Props> = ({ initialRoles, onRolesLoaded }) => {
  const { user } = useAuth();
  const { showAlert } = useModal();
  const canManage = isChangeManager(user);
  const [roles, setRoles] = useState<PostImplEditorRole[]>(
    normalizePostImplEditorRoles(initialRoles),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialRoles) {
      setRoles(normalizePostImplEditorRoles(initialRoles));
      return;
    }
    let cancelled = false;
    void changesApi
      .getPostImplFormSchema()
      .then((data) => {
        if (cancelled) return;
        const next = normalizePostImplEditorRoles(data.post_impl_editor_roles);
        setRoles(next);
        onRolesLoaded?.(next);
      })
      .catch(() => {
        if (!cancelled) setRoles([...DEFAULT_POST_IMPL_EDITOR_ROLES]);
      });
    return () => {
      cancelled = true;
    };
  }, [initialRoles, onRolesLoaded]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await changesApi.updatePostImplEditorRoles({
        post_impl_editor_roles: roles,
      });
      const next = normalizePostImplEditorRoles(result.post_impl_editor_roles);
      setRoles(next);
      await showAlert({
        title: 'Permissões salvas',
        message: 'Quem pode responder o registro pós-implantação foi atualizado.',
      });
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar as permissões.'));
    } finally {
      setSaving(false);
    }
  }, [roles, showAlert]);

  return (
    <Card className="mb-4">
      <Card.Body>
        <p className="mb-2 fw-semibold">Quem responde o registro pós-implantação</p>
        <p className="text-muted small mb-3">
          Em <strong>Revisar</strong>, administrador e gestor de mudanças podem gravar em qualquer
          mudança. Dono e solicitante só na mudança em que são dono ou solicitante. Mantenha ao
          menos uma opção marcada.
        </p>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2">
            {error}
          </Alert>
        ) : null}
        <div className="mb-3 d-flex flex-column gap-2">
          {POST_IMPL_EDITOR_ROLE_OPTIONS.map((item) => {
            const checked = roles.includes(item.value);
            return (
              <Form.Check
                key={item.value}
                type="checkbox"
                id={`post-impl-editor-${item.value}`}
                label={item.label}
                checked={checked}
                disabled={!canManage}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...roles, item.value]
                    : roles.filter((role) => role !== item.value);
                  setRoles(
                    next.length ? normalizePostImplEditorRoles(next) : [...DEFAULT_POST_IMPL_EDITOR_ROLES],
                  );
                }}
              />
            );
          })}
        </div>
        {canManage ? (
          <div className="d-flex justify-content-end">
            <Button size="sm" disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Salvando...' : 'Salvar permissões'}
            </Button>
          </div>
        ) : (
          <p className="text-muted small mb-0">Somente gestor de mudanças altera este cadastro.</p>
        )}
      </Card.Body>
    </Card>
  );
};

export default ChangePostImplEditorRolesPanel;
